import {
  AnalyticsEvents,
  type ComposerVideoUploadFailedProperties,
} from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { AttachmentEntity } from 'openapi'
import { useCallback, useState } from 'react'
import { useFileUpload, type UseFileUploadOptions } from './use-file-upload'

const MAX_VIDEO_SIZE = 50 * 1024 * 1024
const MAX_DURATION_SECONDS = 30
// Tiny grace so a clip that's effectively 30s but reports 30.04s isn't punished.
const DURATION_GRACE_SECONDS = 0.5
// 9 / 16 = 0.5625. 0.02 lets us accept 1080×1920 (0.5625) plus tiny encoder drift
// without accepting square (1.0) or 4:5 (0.8) by mistake.
const ASPECT_TARGET = 9 / 16
const ASPECT_TOLERANCE = 0.02

export type VideoValidationCode = 'format' | 'size' | 'duration' | 'aspect' | 'metadata'

const ERROR_COPY: Record<VideoValidationCode, string> = {
  format: 'Use an MP4 video.',
  size: 'Video is too large (max 50 MB).',
  duration: 'Video is too long (max 30 seconds).',
  aspect: 'Video must be vertical 9:16.',
  metadata: "Couldn't read this video. Try a different file.",
}

export class VideoValidationError extends Error {
  readonly code: VideoValidationCode
  constructor(code: VideoValidationCode) {
    super(ERROR_COPY[code])
    this.code = code
  }
}

interface VideoMetadata {
  duration: number
  width: number
  height: number
}

const readVideoMetadata = (file: File): Promise<VideoMetadata> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true

    const cleanup = () => {
      URL.revokeObjectURL(url)
      video.removeAttribute('src')
      video.load()
    }

    video.onloadedmetadata = () => {
      const { duration, videoWidth: width, videoHeight: height } = video
      cleanup()
      if (!Number.isFinite(duration) || width === 0 || height === 0) {
        reject(new VideoValidationError('metadata'))
        return
      }
      resolve({ duration, width, height })
    }
    video.onerror = () => {
      cleanup()
      reject(new VideoValidationError('metadata'))
    }
    video.src = url
  })

const validateReelsVideo = async (file: File): Promise<void> => {
  if (file.type !== 'video/mp4') throw new VideoValidationError('format')
  if (file.size > MAX_VIDEO_SIZE) throw new VideoValidationError('size')

  const { duration, width, height } = await readVideoMetadata(file)

  if (duration > MAX_DURATION_SECONDS + DURATION_GRACE_SECONDS) {
    throw new VideoValidationError('duration')
  }
  if (Math.abs(width / height - ASPECT_TARGET) > ASPECT_TOLERANCE) {
    throw new VideoValidationError('aspect')
  }
}

export type VideoUploadMethod = 'button' | 'drag' | 'paste'

interface UseVideoUploadOptions extends Omit<UseFileUploadOptions, 'allowedTypes' | 'maxSize'> {
  /** Per-platform editor that opened the picker. Used for analytics. */
  platform?: string
  /** Creative the video will attach to. Used for analytics. */
  creativeId?: string
  /** Creative shape (e.g. facebook_post). Used for analytics. */
  creativeType?: string
  /** Editor field the upload feeds (typically 'video'). Used for analytics. */
  fieldId?: string
  /** How the file entered the picker. Defaults to 'button'. */
  uploadMethod?: VideoUploadMethod
}

type FailureErrorType = ComposerVideoUploadFailedProperties['error_type']

// Map an inner upload throw to a coarse error_type. We don't have HTTP status
// at this layer (apiCall surfaces strings/Errors), so we lean on the message
// shape: the inner hook throws explicit "Invalid file type" / "File size…"
// for client-side rejects, everything else is unknown.
function classifyInnerError(err: unknown): FailureErrorType {
  if (err instanceof Error) {
    if (/^Invalid file type/i.test(err.message)) return 'validation'
    if (/^File size/i.test(err.message)) return 'size_limit'
    if (/network|fetch|failed to fetch/i.test(err.message)) return 'network'
    // Status-coded errors thrown by apiCall sometimes look like "500 …" / "503 …"
    const status = err.message.match(/\b(4\d\d|5\d\d)\b/)?.[1]
    if (status) return status.startsWith('5') ? 'server' : 'validation'
  }
  return 'unknown'
}

export const useVideoUpload = (options: UseVideoUploadOptions = {}) => {
  const {
    onSuccess,
    onError,
    platform,
    creativeId,
    creativeType,
    fieldId = 'video',
    uploadMethod = 'button',
  } = options
  const { track } = useAnalytics()
  const [validationError, setValidationError] = useState<VideoValidationError | null>(null)

  // Server enforces 50 MB; we duplicate here so the user sees a typed error
  // before we kick off the upload. We pass through onError so the underlying
  // hook still notifies callers if the network step fails.
  const inner = useFileUpload({
    allowedTypes: ['video/mp4'],
    maxSize: MAX_VIDEO_SIZE,
    onSuccess,
    onError,
  })

  const handleUpload = useCallback(
    async (file: File): Promise<AttachmentEntity> => {
      setValidationError(null)

      const startedAt =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now()

      track(AnalyticsEvents.Composer.COMPOSER_VIDEO_UPLOAD_STARTED, {
        creative_id: creativeId ?? '',
        creative_type: creativeType,
        platform,
        field_id: fieldId,
        file_size_bytes: file.size,
        file_mime: file.type,
        upload_method: uploadMethod,
      })

      try {
        await validateReelsVideo(file)
      } catch (err) {
        const verr =
          err instanceof VideoValidationError ? err : new VideoValidationError('metadata')
        setValidationError(verr)
        track(AnalyticsEvents.Composer.COMPOSER_VIDEO_UPLOAD_FAILED, {
          creative_id: creativeId ?? '',
          creative_type: creativeType,
          platform,
          field_id: fieldId,
          error_type: verr.code,
          error_message: verr.message,
          file_size_bytes: file.size,
        })
        onError?.(verr.message)
        throw verr
      }

      try {
        const attachment = await inner.handleUpload(file)
        const durationMs = Math.round(
          (typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now()) - startedAt
        )
        track(AnalyticsEvents.Composer.COMPOSER_VIDEO_UPLOAD_COMPLETED, {
          creative_id: creativeId ?? '',
          creative_type: creativeType,
          platform,
          field_id: fieldId,
          duration_ms: durationMs,
          file_size_bytes: file.size,
        })
        return attachment
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        track(AnalyticsEvents.Composer.COMPOSER_VIDEO_UPLOAD_FAILED, {
          creative_id: creativeId ?? '',
          creative_type: creativeType,
          platform,
          field_id: fieldId,
          error_type: classifyInnerError(err),
          error_message: message,
          file_size_bytes: file.size,
        })
        throw err
      }
    },
    [inner, onError, track, platform, creativeId, creativeType, fieldId, uploadMethod]
  )

  return {
    handleUpload,
    isUploading: inner.isUploading,
    error: inner.error,
    validationError,
    resetValidationError: () => setValidationError(null),
  }
}
